/* eslint-disable no-console */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ENVIRONMENT } from 'config';
import { selectAuthUser, selectSessionId } from 'modules/common/auth/selectors';
import { useSelector } from 'react-redux';
import {
  retrieveVisualLoadDuration,
  storeVisualMapper,
} from 'modules/common/utils/filter-visual-mapper';
import logger from 'modules/common/utils/logger';
import { selectTabValue } from '../../../selectors';
import { TRUE, VISUAL_EVENTS } from '../../../constants';
import useGlobalPbiFilters from '../../../hooks/use-global-pbi-filters';
// import { REPORT_TYPE } from '../../../constants';
// import { isEmpty } from '../../../../common/helpers/object';

/**
 * Power BI visual component
 * @param {String} cssClassName - Class name for the visual
 * @param {String} accessToken - Power bi access token
 * @param {String} embedUrl - Power bi embed url
 * @param {String} visualName - Power BI visual name (used for logging)
 * @param {String} id - Power bi report id
 * @param {String} pageName - Power bi page name
 * @param {Array} filters - List of filters to be applied for the visual
 * @param {String} reportName - Power BI report name (used for logging)
 * @param {String} pageDisplayName - Power BI page display name (used for logging)
 * @param {Function|null} dispatch - Redux dispatch (used for error recovery)
 * @param {Function} setPBIReport - Function to set loaded report object
 * @param {Function} setIsLoaded - Function to set report loaded state
 * @param {Function} setIsRendered - Function to set visual rendered state
 * @param {Function} displayFunction - Function to toggle visual display in error scenarios
 * @param {String|null} tab - Active tab identifier used to gate rendering/listeners
 * @returns {JSX.Element}
 */

const VisualCard = (props) => {
  const {
    cssClassName,
    accessToken,
    embedUrl,
    visualName,
    id,
    pageName,
    filters,
    reportName,
    pageDisplayName,
    dispatch = null,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    setIsRendered = () => {},
    displayFunction = () => {},
    // reportType = '',
    tab = null,
  } = props;
  // global selectors
  const user = useSelector(selectAuthUser);
  const sessionId = useSelector(selectSessionId);
  const tabValue = useSelector(selectTabValue);
  const globalFilters = useGlobalPbiFilters();
  //
  const reportRef = useRef(null);
  const visualRenderedCountRef = useRef(new Map());
  const prevFiltersRef = useRef(null);

  /**
   * Local structured logger helper.
   */
  const visualLog = useCallback(
    ({ name, level = 'info', context = {} }) => {
      if (user?.username) {
        logger.setUser({ username: user.username });
      }
      const payload = {
        name,
        level,
        context: {
          sessionId,
          visualName,
          reportName,
          pageDisplayName,
          ...context,
        },
      };
      if (ENVIRONMENT.PBI_EMBED_DEBUG_MODE?.toLowerCase() === TRUE) {
        console.log(payload);
      }
      logger.logEvent(payload);
    },
    [user, sessionId, visualName, reportName, pageDisplayName]
  );
  /**
   * Initial filters (prevents double render)
   */
  const initialFilters = useMemo(() => {
    if (tabValue === tab && filters && globalFilters?.length) {
      return [...filters, ...globalFilters];
    }
    return undefined;
  }, [tabValue, tab, filters, globalFilters]);
  /**
   * Store visual start timestamp in localStorage.
   * This is used later to compute the duration until the report/visual emits rendered/loaded events.
   */
  useEffect(() => {
    if (tabValue !== tab) return;
    if (!initialFilters || initialFilters.length === 0) return;

    storeVisualMapper();
  }, [initialFilters, tabValue, tab]);
  /**
    Register Power BI embedded report event listeners.
    Listens for:
     'rendered': Logs render timing + increments render count for the visual.
     'loaded': Logs when the report is fully loaded.
     'error': Logs detailed embed errors.
     'visualRendered': Logs when a specific visual within the report is rendered.
    Listeners are attached only for the active tab and cleaned up on dependency changes/unmount.
     */
  useEffect(() => {
    const report = reportRef.current;
    const visualRenderedCount = visualRenderedCountRef.current;
    //
    if (!report || tabValue !== tab) {
      return () => {}; // always return a function
    }
    //

    /**
     * Handles the Power BI 'rendered' event.
     * Increments per-visual render count, logs duration since last filter dispatch, and updates rendered state.
     *
     * @param {Object} event - Power BI embed event payload.
     */
    const handleRendered = (event) => {
      const currentDate = new Date();
      const dispatchDuration = retrieveVisualLoadDuration(currentDate);
      //
      const key = `${visualName}_${reportName}_${pageDisplayName}`;

      visualRenderedCount.set(key, (visualRenderedCount.get(key) || 0) + 1);

      visualLog({
        name: 'pbi_rendered',
        level: 'info',
        context: {
          timestamp: currentDate.toISOString(),
          renderCount: visualRenderedCount.get(key),
          durationSinceFilterDispatch: dispatchDuration,
        },
      });
      //
      if (event.target.classList.contains('loader')) {
        event.target.classList.remove('loader');
      }
      setIsRendered(true);
    };

    /**
     * Handles the Power BI 'loaded' event.
     * Logs duration since last filter dispatch and updates loaded state.
     */
    const handleLoaded = () => {
      const currentDate = new Date();
      const dispatchDuration = retrieveVisualLoadDuration(currentDate);

      visualLog({
        name: 'pbi_loaded',
        level: 'info',
        context: {
          timestamp: currentDate.toISOString(),
          durationSinceFilterDispatch: dispatchDuration,
        },
      });

      setIsLoaded(true);
    };

    /**
     * Handles the Power BI 'error' event.
     * Logs the detailed embed error message (if available).
     *
     * @param {Object} event - Power BI embed error event payload.
     */
    const handleError = (event) => {
      const currentDate = new Date();
      visualLog({
        name: 'pbi_error',
        level: 'error',
        context: {
          timestamp: currentDate.toISOString(),
          detailedMessage: event?.detail?.detailedMessage,
        },
      });
    };

    /**
     * Handles the Power BI 'visualRendered' event.
     * Logs the event and increments render count when debug mode is enabled.
     */
    const handleVisualRendered = () => {
      const currentDate = new Date();
      visualLog({
        name: 'pbi_visual_rendered',
        level: 'info',
        context: {
          timestamp: currentDate.toISOString(),
        },
      });
      //
      if (ENVIRONMENT.PBI_EMBED_DEBUG_MODE?.toLowerCase() === TRUE) {
        visualRenderedCount.set(
          `${visualName}_${reportName}_${pageDisplayName}`,
          (visualRenderedCount.get(`${visualName}_${reportName}_${pageDisplayName}`) || 0) + 1
        );
      }
    };

    // Attach event listeners
    report.on(VISUAL_EVENTS.RENDERED, handleRendered);
    report.on(VISUAL_EVENTS.LOADED, handleLoaded);
    report.on(VISUAL_EVENTS.ERROR, handleError);
    report.on(VISUAL_EVENTS.VISUAL_RENDERED, handleVisualRendered);
    //
    return () => {
      if (report) {
        // Remove ALL listeners defensively
        report.off(VISUAL_EVENTS.RENDERED, handleRendered);
        report.off(VISUAL_EVENTS.LOADED, handleLoaded);
        report.off(VISUAL_EVENTS.ERROR, handleError);
        report.off(VISUAL_EVENTS.VISUAL_RENDERED, handleVisualRendered);
      }
      prevFiltersRef.current = null;
    };
  }, [tabValue, tab, visualLog]);

  // Applies updated filters to the embedded report when the active tab/filters change.
  const applyFilters = useCallback(async () => {
    const report = reportRef.current;

    if (!report || !filters || !globalFilters?.length || tabValue !== tab) {
      return;
    }
    storeVisualMapper();
    //
    const updatedFilter = [...filters, ...globalFilters];
    // Prevent redundant updates
    if (JSON.stringify(prevFiltersRef.current) === JSON.stringify(updatedFilter)) {
      return;
    }
    prevFiltersRef.current = updatedFilter;
    try {
      setIsRendered(false);
      //
      await report.updateFilters(models.FiltersOperations.ReplaceAll, updatedFilter);
      //
      const currentDate = new Date();
      visualLog({
        name: 'pbi_filter_dispatched',
        level: 'info',
        context: {
          timestamp: currentDate.toISOString(),
        },
      });
    } catch (error) {
      const currentDate = new Date();
      visualLog({
        name: 'pbi_default_filter_update_failed',
        level: 'error',
        context: {
          timestamp: currentDate.toISOString(),
          errorMessage: error?.message ?? 'Unknown error',
          filtersSnapshot: JSON.stringify(updatedFilter),
          activeTabValue: tabValue,
          reportId: report?.config?.id,
          pageId: report?.config?.pageName,
        },
      });
      // Forced UI refresh hack — investigate root cause in dependencies
      dispatch(displayFunction(false));
      setTimeout(() => {
        dispatch(displayFunction(true));
      }, 200);
    }
  }, [filters, globalFilters, tabValue, tab, visualLog]);
  //
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);
  //
  return (
    <PowerBIEmbed
      embedConfig={{
        type: 'report',
        tokenType: models.TokenType.Embed,
        accessToken,
        embedUrl,
        id,
        pageName,
        filters: initialFilters,
        settings: {
          displayOption: models.DisplayOption.FitToPage,
          visualRenderedEvents: ENVIRONMENT.ENABLE_PBI_VISUAL_RENDERED_EVENT === TRUE,
          panes: {
            filters: {
              expanded: false,
              visible: false,
            },
            pageNavigation: {
              visible: false,
            },
          },
          background: models.BackgroundType.Transparent,
        },
      }}
      cssClassName={cssClassName}
      getEmbeddedComponent={(report) => {
        reportRef.current = report;
        setPBIReport(report);
      }}
    />
  );
};
//
export default VisualCard;
